import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from "@cieda/djayma-ui";
import type { Product } from "@/lib/validation/product";

interface BulkThresholdModalProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly products: readonly Product[];
  /**
   * Called with the new threshold value, or `null` to reset to default.
   */
  readonly onConfirm: (threshold: number | null) => void;
  readonly isLoading: boolean;
}

export function BulkThresholdModal({
  open,
  onOpenChange,
  products,
  onConfirm,
  isLoading,
}: BulkThresholdModalProps) {
  const { t } = useTranslation("admin");
  const [rawValue, setRawValue] = useState("");
  const [resetToDefault, setResetToDefault] = useState(false);

  useEffect(() => {
    if (!open) {
      setRawValue("");
      setResetToDefault(false);
    }
  }, [open]);

  const parsed = Number.parseInt(rawValue, 10);
  const hasValidNumber =
    rawValue !== "" && Number.isFinite(parsed) && parsed >= 0;
  const isValid = resetToDefault || hasValidNumber;

  const preview = products.slice(0, 5);
  const remaining = products.length - preview.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t("products.bulk.threshold")} ({products.length} produit(s))
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="bulk-threshold-input">
              {t("products.bulk.threshold_input")}
            </Label>
            <Input
              id="bulk-threshold-input"
              type="number"
              inputMode="numeric"
              min={0}
              value={rawValue}
              onChange={(e) => setRawValue(e.target.value)}
              disabled={resetToDefault}
              autoComplete="off"
            />
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="bulk-threshold-reset"
              checked={resetToDefault}
              onCheckedChange={(checked) => setResetToDefault(checked === true)}
            />
            <Label
              htmlFor="bulk-threshold-reset"
              className="cursor-pointer text-sm font-normal"
            >
              {t("products.bulk.threshold_reset")}
            </Label>
          </div>

          <ul className="space-y-1 text-sm text-stone-700">
            {preview.map((p) => (
              <li key={p.id}>{p.name}</li>
            ))}
            {remaining > 0 && (
              <li className="text-stone-400">et {remaining} autre(s)</li>
            )}
          </ul>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button
            disabled={!isValid || isLoading}
            onClick={() => onConfirm(resetToDefault ? null : parsed)}
          >
            Appliquer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
